import React, { useEffect, useMemo, useState } from 'react'
import { Link } from 'react-router-dom'
import { AlertTriangle } from 'lucide-react'
import { api } from '../../lib/api'
import { clickableRowProps } from '../../lib/a11y'
import { useAuth } from '../../context/AuthContext'
import { KpiCard, RiskBadge, SessionRail, EmptyState, ProgressBar, Section } from '../../components/ui'
import { CaseDrawer } from '../../components/CaseDrawer'
import { ProviderCredentialingPanel } from '../../components/ProviderCredentialingPanel'
import type { ContinuityAlert, ContinuityCase, Patient, Provider, ProviderReadiness, ProviderDiscoverability, ProviderMetrics, SessionEvent } from '../../lib/types'

function startOfToday(): Date {
  const d = new Date()
  d.setHours(0, 0, 0, 0)
  return d
}

function isToday(iso: string | null): boolean {
  if (!iso) return false
  const d = new Date(iso)
  const today = startOfToday()
  return d >= today && d < new Date(today.getTime() + 24 * 60 * 60 * 1000)
}

export default function ProviderDashboard() {
  const { me } = useAuth()
  const [alerts, setAlerts] = useState<ContinuityAlert[]>([])
  const [cases, setCases] = useState<ContinuityCase[]>([])
  const [patients, setPatients] = useState<Patient[]>([])
  const [sessions, setSessions] = useState<SessionEvent[]>([])
  const [myProvider, setMyProvider] = useState<Provider | null>(null)
  const [readiness, setReadiness] = useState<ProviderReadiness | null>(null)
  const [discoverability, setDiscoverability] = useState<ProviderDiscoverability | null>(null)
  const [metrics, setMetrics] = useState<ProviderMetrics | null>(null)
  const [openCaseId, setOpenCaseId] = useState<string | null>(null)
  const [loading, setLoading] = useState(true)
  const [search, setSearch] = useState('')

  async function load() {
    setLoading(true)
    const [a, c, pt, sess] = await Promise.all([
      api('/v1/risk-alerts?status=open'),
      api('/v1/cases'),
      api('/patients'),
      api('/v1/session-events'),
    ])
    setAlerts(a); setCases(c); setPatients(pt); setSessions(sess)
    if (me?.provider_id) {
      const [provider, r, d, m] = await Promise.all([
        api(`/providers/${me.provider_id}`),
        api(`/providers/${me.provider_id}/readiness`),
        api(`/providers/${me.provider_id}/discoverability`),
        api('/v1/provider/metrics'),
      ])
      setMyProvider(provider); setReadiness(r); setDiscoverability(d); setMetrics(m)
    }
    setLoading(false)
  }

  useEffect(() => { load() }, [])

  function patientName(id: string) { return patients.find((p) => p.id === id)?.name || 'Patient' }

  const todaysSessions = sessions
    .filter((s) => isToday(s.scheduled_at) && ['scheduled', 'confirmed'].includes(s.status))
    .sort((a, b) => new Date(a.scheduled_at!).getTime() - new Date(b.scheduled_at!).getTime())

  const retained = cases.filter((c) => c.reached_session_4).length

  const filteredCases = useMemo(() => {
    if (!search.trim()) return cases
    const q = search.trim().toLowerCase()
    return cases.filter((c) => patientName(c.patient_id).toLowerCase().includes(q))
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [cases, search, patients])

  if (loading) return <div className="page-loading">Loading your continuity queue…</div>

  return (
    <div>
      <div className="page-head">
        <h1>Today's continuity queue</h1>
        <div className="ph-sub">Your assigned cases, prioritized by continuity risk.</div>
      </div>

      <div className="kpis">
        <KpiCard label="My patients" value={cases.length} />
        <KpiCard label="At risk" value={alerts.length} />
        <KpiCard label="Reached Session 4" value={retained} />
      </div>

      <div className="section-block">
        <div className="section-head"><h2>Today's Schedule</h2></div>
        {todaysSessions.length === 0 ? (
          <EmptyState>No sessions scheduled today.</EmptyState>
        ) : (
          <div className="row-list">
            {todaysSessions.map((s) => (
              <div key={s.id} className="row">
                <span>{patientName(s.patient_id || '')}</span>
                {/* UTC-pinned to match availability_engine.py's slot generation —
                    see BookAppointment.tsx's timeLabel comment for why. */}
                <span className="muted">{new Date(s.scheduled_at!).toLocaleTimeString(undefined, { hour: 'numeric', minute: '2-digit', timeZone: 'UTC' })}</span>
              </div>
            ))}
          </div>
        )}
      </div>

      <div className="section-block">
        <div className="section-head"><h2>Today's priorities</h2><span className="sh-note">{alerts.length} need action</span></div>
        {alerts.length === 0 ? (
          <EmptyState icon={<AlertTriangle size={16} />}>No open alerts on your caseload.</EmptyState>
        ) : (
          <div className="queue-list">
            {alerts.map((a) => (
              <div key={a.id} className="queue-item" {...clickableRowProps(() => setOpenCaseId(a.case_id))}>
                <div className="qi-main">
                  <span>{patientName(a.patient_id)}</span>
                  <span className="qi-reason">{a.reason}</span>
                </div>
                <RiskBadge band={a.risk_band} />
              </div>
            ))}
          </div>
        )}
      </div>

      {metrics && (
        <Section title="My continuity performance">
          <div className="kpis">
            <KpiCard
              label="Continuity rate"
              value={metrics.total_cases > 0 ? `${Math.round((metrics.active_cases / metrics.total_cases) * 100)}%` : '—'}
              hint="active cases / total cases"
            />
            <KpiCard
              label="Session 4 conversion"
              value={metrics.session_4_conversion_rate != null ? `${Math.round(metrics.session_4_conversion_rate * 100)}%` : '—'}
            />
            <KpiCard label="At-risk cases" value={metrics.at_risk_count} />
          </div>
          <div className="table-wrap" style={{ marginTop: 12 }}>
            <table className="data-table">
              <thead><tr><th>Match band</th><th>Total cases</th><th>Reached Session 4</th><th>Retention rate</th></tr></thead>
              <tbody>
                {Object.entries(metrics.retention_by_match_band).map(([band, row]) => (
                  <tr key={band}>
                    <td style={{ textTransform: 'capitalize' }}>{band}</td>
                    <td>{row.total}</td>
                    <td>{row.retained}</td>
                    <td>{row.rate != null ? `${Math.round(row.rate * 100)}%` : '—'}</td>
                  </tr>
                ))}
                {Object.keys(metrics.retention_by_match_band).length === 0 && (
                  <tr><td colSpan={4} className="muted">No cases with a recorded match band yet.</td></tr>
                )}
              </tbody>
            </table>
          </div>
        </Section>
      )}

      {/* Persistent readiness card — never a hard gate. A provider always
          reaches this dashboard regardless of onboarding completeness; this
          card just keeps nudging until they're both 100% ready and
          discoverable to patients, since those are two different bars
          (e.g. finishing onboarding without a calendar clears the first
          but not the second). */}
      {readiness && discoverability && (readiness.readiness_score < 100 || !discoverability.discoverable) && (
        <div className="section-block warm-theme">
          <div className="card">
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 16, flexWrap: 'wrap', marginBottom: 12 }}>
              <div>
                <h3 style={{ margin: '0 0 4px' }}>Complete Your Profile</h3>
                <p className="muted" style={{ margin: 0, fontSize: 13.5 }}>Your progress is saved as you go, so there's no rush.</p>
              </div>
              <Link className="btn" to="/practice/provider/onboarding">Continue setup</Link>
            </div>

            <ProgressBar label="Profile Completion" percent={readiness.readiness_score} />

            {readiness.remaining.length > 0 && (
              <div style={{ marginTop: 12 }}>
                <p className="muted" style={{ fontSize: 12.5, fontWeight: 600, marginBottom: 4 }}>Remaining:</p>
                <ul style={{ margin: 0, paddingLeft: 18 }}>
                  {readiness.remaining.map((r) => <li key={r} style={{ fontSize: 13.5 }}>{r}</li>)}
                </ul>
              </div>
            )}

            <div style={{ marginTop: 14, display: 'flex', alignItems: 'center', gap: 8 }}>
              <span className={`badge ${discoverability.discoverable ? 'badge-green' : 'badge-red'}`}>
                {discoverability.discoverable ? 'Discoverable to patients' : 'Not Discoverable'}
              </span>
            </div>
            {!discoverability.discoverable && discoverability.missing.length > 0 && (
              <div style={{ marginTop: 8 }}>
                <p className="muted" style={{ fontSize: 12.5, fontWeight: 600, marginBottom: 4 }}>Missing (required to appear in patient matching):</p>
                <ul style={{ margin: 0, paddingLeft: 18 }}>
                  {discoverability.missing.map((m) => <li key={m} style={{ fontSize: 13.5 }}>{m}</li>)}
                </ul>
              </div>
            )}
          </div>
        </div>
      )}

      {myProvider && (
        <div className="section-block">
          <div className="section-head"><h2>Professional verification</h2></div>
          <div className="card">
            <ProviderCredentialingPanel provider={myProvider} editable onUpdated={load} />
          </div>
        </div>
      )}

      <div className="section-block">
        <div className="section-head"><h2>My patients</h2><span className="sh-note">click any patient for detail</span></div>
        <input
          className="inp"
          style={{ maxWidth: 280, marginBottom: 10 }}
          placeholder="Search by patient name…"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
        />
        <div className="row-list">
          {filteredCases.map((c) => (
            <div key={c.id} className="row" {...clickableRowProps(() => setOpenCaseId(c.id))}>
              <span>{patientName(c.patient_id)}</span>
              <SessionRail count={c.session_count} />
            </div>
          ))}
          {filteredCases.length === 0 && (
            <EmptyState>{cases.length === 0 ? 'No cases assigned yet.' : 'No patients match this search.'}</EmptyState>
          )}
        </div>
      </div>

      {openCaseId && (
        <CaseDrawer
          caseId={openCaseId}
          onClose={() => { setOpenCaseId(null); load() }}
          patientName={patientName(cases.find((c) => c.id === openCaseId)?.patient_id || '')}
        />
      )}
    </div>
  )
}
